import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Stack,
  Button,
  Divider,
  Typography,
  Box,
  IconButton,
  alpha,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Checkbox,
  ListItemText,
  styled,
  ListItemButton,
} from '@mui/material';
import Iconify from '../common/iconify.component';
import Scrollbar from '../common/scrollbar.component';
import {
  DECADES,
  FILTER_SELECT_FIELD_CONDITION,
  URL_CHECK,
} from '../../config/const.config';
import Decades from '../form-elements/decades.component';
import Countries from '../form-elements/countries.component';
import Regions from '../form-elements/regions.component';
import VideoTypes from '../form-elements/video-types.component';
import Trashy from '../form-elements/trashy.component';
import GoodBad from '../form-elements/good-bad.component';
import Booleans from '../form-elements/boolean.component';
import MpaaRating from '../form-elements/mpaa-rating.component';
import RestrictedContent from '../form-elements/restricted-content.component';
import RestrictedContentReason from '../form-elements/restricted-content-reason.component';
import AdTimingStatus from '../form-elements/ad-timing-status.component';
import GenreStatus from '../form-elements/genre-status.component';
import Channels from '../form-elements/channels.component';
import UrlCheckType from '../form-elements/url-check-type.component';
import CollapsibleSection from './collapsible-section.component';
import FilterInputCondition from './filter-input-condition.component';
import MultiValueInputCondition from './multi-value-input.component';
import NumberBetweenInput from './number-between-input.component';
import DateBetweenInput from './date-between-input.component';
import useResponsive from '../../hooks/responsive.hook';
import PrimaryGenres from '../form-elements/primary-genres.component';
import SecondaryGenres from '../form-elements/secondary-genres.component';
import PublicDomainAutocomplete from '../form-elements/public-domain-autocomplete.component';
import AlwaysPublished from '../form-elements/always-published.component';
import LanguageCodes from '../form-elements/language-codes.component';
import TVODPrice from '../form-elements/tvod-price.component';
import Poster16x9Status from '../form-elements/poster-16x9-status.component';
import VideoFilterSelectCondition from '../form-elements/video-filter-select-condition.component';
import VideoSources from '../form-elements/video-sources.component';

const StyledItem = styled((props) => (
  <ListItemButton disableGutters disableRipple {...props} />
))(({ theme }) => ({
  ...theme.typography.body2,
  height: 36,
  position: 'relative',
  textTransform: 'capitalize',
  color: 'black',
  borderRadius: theme.shape.borderRadius,
}));

const FilterPlaylistVideos = ({
  values,
  onCancel,
  onSuccess,
  from,
  isFormSubmitted,
}) => {
  const sectionFilters = {
    popular: ['title_name_only', 'genres', 'channels'],
    videoAttributes: [
      'UUID',
      'date_creation',
      'video_type',
      'description',
      'plot_keywords',
      'imdb_id',
      'duration_minutes',
      'video_language',
      'color',
    ],
    videoClassification: ['c.name', 'year', 'decade', 'v.country', 'region'],
    seriesAttributes: ['s.title', 'season', 'episode_num'],
    owner: [
      'd.username',
      'distributor_sku',
      'distributor_sku_series',
      'contract_no',
      'd.royality',
      'studio',
    ],
    rightsManagement: [
      'date_license_expiration',
      'avod',
      'svod',
      'tvod',
      'avod_validated',
      'tvod_enabled',
      'tvod_iap',
      'geofencing_allow',
      'geofencing_deny',
    ],
    videoAdLiveStatus: [
      'ad_timing_status',
      'avod_enabled',
      'offline',
      'offline_reason',
    ],
    editorial: [
      'restricted_content',
      'restricted_content_reason',
      'exclude_from_db',
    ],
    rating: ['good_bad', 'trashy', 'five_star_rating', 'mpaa_rating'],
    publicDomain: ['public_domain', 'custom_royality'],
    tools: ['duplicate_imdb', 'licenese_expired_videos', 'always_published'],
    castCrew: ['directors', 'writers', 'cast'],
    admin: ['mm_notes', 'ms_notes'],
    metadatStatus: ['genre_status', 'description_status', 'poster_16x9_status'],
    assetUrl: [
      'video_url',
      'trailer_url',
      'poster_16x9_url',
      'subtitle_en_url',
    ],
    assetUrlStatus: [
      'quality_video',
      'quality_subtitle',
      'quality_poster_16x9',
      'quality_trailer',
      'quality_source_url',
    ],
    analytics: ['avod_watched', 'svod_watched'],
  };

  const isDesktop = useResponsive('up', 'lg');

  const [filters, setFilters] = useState(values || {});
  const [selectedPrimaryGenre, setSelectedPrimaryGenre] = useState(null);
  const [selectedSecondaryGenre, setSelectedSecondaryGenre] = useState(null);

  const filterObject = filters.genres
    ? {
        ...filters.genres,
        value: filters.genres.value || [],
        condition: filters.genres.condition || 'AND',
      }
    : {
        value: [],
        condition: 'AND',
        type: 'multiSelectType',
        apply: false,
      };

  const handleAddValue = () => {
    setFilters((prev) => ({
      ...prev,
      genres: {
        ...filterObject,
        type: 'multiSelectType',
        value: [
          ...new Set([
            ...filterObject.value,
            `${
              selectedPrimaryGenre?.name === '* (All)'
                ? '*'
                : selectedPrimaryGenre.name
            } | ${
              // eslint-disable-next-line no-nested-ternary
              selectedSecondaryGenre?.name ===
              '@ (Primary + All Secondary Genres)'
                ? '@'
                : // eslint-disable-next-line no-nested-ternary
                selectedSecondaryGenre?.name === '* (All)'
                ? '*'
                : selectedSecondaryGenre?.name === '/ (None)'
                ? '/'
                : selectedSecondaryGenre?.name
            }`,
          ]),
        ],
      },
    }));
  };

  const handleRemoveValue = (val) => {
    setFilters((prev) => ({
      ...prev,
      genres: {
        ...filterObject,
        value: filterObject.value.filter((item) => item !== val),
      },
    }));
  };

  useEffect(() => {
    setFilters(values || {});
  }, [values]);

  const handleChange = (e, id) => {
    setFilters((prev) => ({
      ...prev,
      [id]: {
        ...(prev[id] || {}),
        value: e?.target?.value || e,
        condition: 'EQUALS_TO',
        type: 'selectType',
        apply: prev[id]?.apply || false,
      },
    }));
  };

  const getDefaultValue = (id) => filters[id]?.value || '';

  const handleApplyFilters = () => {
    onSuccess(filters);
  };

  const hasAppliedFilters = (sectionKey) =>
    sectionFilters[sectionKey]?.some(
      (field) =>
        filters[field]?.apply === true &&
        ((filters[field]?.type === 'checkbox' && filters[field]?.value) ||
          filters[field]?.type !== 'checkbox')
    );

  return (
    <>
      <Stack
        direction="row"
        alignItems="center"
        sx={{
          px: 1,
          py: 2,
          // eslint-disable-next-line no-nested-ternary
          minHeight: !isDesktop
            ? '76px'
            : from !== 'video-assignment' && from !== 'edit-videos-list'
            ? '92px'
            : '60px',
        }}
      >
        <Typography variant="subtitle1" sx={{ ml: 1, textAlign: 'center' }}>
          Filters
        </Typography>
        {from !== 'video-assignment' && from !== 'edit-videos-list' && (
          <IconButton
            color="inherit"
            variant="contained"
            sx={{
              marginLeft: 2,
              borderRadius: '100%',
              width: 'auto',
              padding: '8px',
              bgcolor: (theme) => alpha(theme.palette.grey[400], 1),
              '&:hover': {
                bgcolor: (theme) => alpha(theme.palette.grey[400], 1),
                boxShadow: 'none',
              },
            }}
            onClick={onCancel}
          >
            <Iconify icon="mdi:menu-close" />
          </IconButton>
        )}
      </Stack>

      <Divider sx={{ borderStyle: 'dashed' }} />

      <Scrollbar
        sx={{
          '& .simplebar-content': {
            height: 1,
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <form id="list-data-filters" onSubmit={(e) => e.preventDefault()}>
          <Stack sx={{ p: 2 }}>
            <CollapsibleSection
              title="Popular"
              appliedFilter={hasAppliedFilters('popular')}
            >
              <Box sx={{ marginBottom: 1 }}>
                <CollapsibleSection
                  id="title_name_only"
                  title="Title"
                  subSection
                  filters={filters}
                  setFilters={setFilters}
                >
                  <FilterInputCondition
                    id="title_name_only"
                    name="titleSearch"
                    filters={filters}
                    setFilters={setFilters}
                  />
                </CollapsibleSection>
                <CollapsibleSection
                  title="Genres"
                  id="genres"
                  subSection
                  filters={filters}
                  setFilters={setFilters}
                >
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{ flexWrap: 'wrap', mb: 1 }}
                  >
                    {filterObject.value?.length > 0 &&
                      filterObject.value.map((val) => (
                        <Chip
                          key={val}
                          label={val}
                          onDelete={() => handleRemoveValue(val)}
                          sx={{ cursor: 'pointer' }}
                        />
                      ))}
                  </Stack>
                  <PrimaryGenres
                    id="primary_genres"
                    name="primary_genres"
                    label="Primary Genre"
                    onChange={(e) => setSelectedPrimaryGenre(e)}
                    sx={{ width: '100%', my: 1 }}
                  />
                  <SecondaryGenres
                    id="secondary_genres"
                    name="secondary_genres"
                    label="Secondary Genre"
                    onChange={(e) => setSelectedSecondaryGenre(e)}
                    sx={{ width: '100%', my: 1 }}
                    primaryGenreId={selectedPrimaryGenre?.id || null}
                  />
                  <Box textAlign="center">
                    <Button
                      size="small"
                      variant="contained"
                      color="secondary"
                      onClick={handleAddValue}
                      sx={{
                        minWidth: 24,
                        padding: '2px 6px',
                        fontSize: '0.7rem',
                        width: 'fit-content',
                      }}
                      disabled={
                        !selectedPrimaryGenre?.name ||
                        !selectedSecondaryGenre?.name
                      }
                    >
                      Add
                    </Button>
                  </Box>
                  <FormControl fullWidth variant="filled" sx={{ my: 1 }}>
                    <InputLabel id="field-genres-select-label">
                      Condition
                    </InputLabel>
                    <Select
                      labelId="demo-simple-select-label"
                      id="g.idConditionType"
                      name="genreConditionType"
                      value={filters.genres?.condition || 'AND'}
                      label="Condition"
                      onChange={(e) => {
                        setFilters((prev) => ({
                          ...prev,
                          genres: {
                            ...prev.genres,
                            condition: e.target.value,
                            type: 'multiSelectType',
                            value: prev.genres?.value || '',
                            apply: prev.genres?.apply || false,
                          },
                        }));
                      }}
                    >
                      <MenuItem key="and" value="AND">
                        AND
                      </MenuItem>
                      <MenuItem key="or" value="OR">
                        OR
                      </MenuItem>
                    </Select>
                  </FormControl>
                </CollapsibleSection>
                <CollapsibleSection
                  title="Assigned To Channel"
                  sx={{
                    borderBottom: 1,
                    borderWidth: '1px',
                    borderColor: 'gray',
                  }}
                  id="channels"
                  subSection
                  filters={filters}
                  setFilters={setFilters}
                >
                  <Channels
                    id="channels"
                    name="channels"
                    label="Channels"
                    defaultValue={getDefaultValue('channels')}
                    multiple
                    onChange={(v) =>
                      setFilters((prev) => ({
                        ...prev,
                        channels: {
                          ...(prev.channels || {}),
                          value: v,
                          condition: 'AND',
                          type: 'multiSelectType',
                          apply: prev.channels?.apply || false,
                        },
                      }))
                    }
                    sx={{ width: '100%', my: 1 }}
                  />
                </CollapsibleSection>
              </Box>
            </CollapsibleSection>
            <CollapsibleSection
              title="Video Attributes"
              appliedFilter={hasAppliedFilters('videoAttributes')}
            >
              <Box sx={{ marginBottom: 1 }}>
                <CollapsibleSection
                  title="UUID"
                  id="UUID"
                  subSection
                  filters={filters}
                  setFilters={setFilters}
                >
                  <FilterInputCondition
                    id="UUID"
                    name="uuidSearch"
                    filters={filters}
                    setFilters={setFilters}
                  />
                </CollapsibleSection>
                <CollapsibleSection
                  title="Date Added"
                  id="date_creation"
                  subSection
                  filters={filters}
                  setFilters={setFilters}
                >
                  <DateBetweenInput
                    id="date_creation"
                    name="dateAdded"
                    filters={filters}
                    setFilters={setFilters}
                  />
                </CollapsibleSection>
                <CollapsibleSection
                  title="Video Type"
                  id="video_type"
                  subSection
                  filters={filters}
                  setFilters={setFilters}
                >
                  <VideoTypes
                    id="video_type"
                    name="videoType"
                    label="Type"
                    defaultValue={getDefaultValue('video_type')}
                    onChange={(e) => handleChange(e, 'video_type')}
                    sx={{ width: '100%', my: 1 }}
                  />
                </CollapsibleSection>
                <CollapsibleSection
                  title="Video Source"
                  id="video_source"
                  subSection
                  filters={filters}
                  setFilters={setFilters}
                >
                  <VideoSources
                    id="video_source"
                    name="videoSource"
                    label="Video Source"
                    defaultValue={getDefaultValue('video_source')}
                    onChange={(e) => handleChange(e, 'video_source')}
                    disabled
                    sx={{ width: '100%', my: 1 }}
                  />
                </CollapsibleSection>
                <CollapsibleSection
                  title="Description"
                  id="description"
                  subSection
                  filters={filters}
                  setFilters={setFilters}
                >
                  <MultiValueInputCondition
                    id="description"
                    name="descriptionSearch"
                    filters={filters}
                    setFilters={setFilters}
                  />
                </CollapsibleSection>
                <CollapsibleSection
                  title="Keywords"
                  id="plot_keywords"
                  subSection
                  filters={filters}
                  setFilters={setFilters}
                >
                  <MultiValueInputCondition
                    id="plot_keywords"
                    name="plotKeywordSearch"
                    filters={filters}
                    setFilters={setFilters}
                  />
                </CollapsibleSection>
                <CollapsibleSection
                  title="IMDB ID"
                  id="imdb_id"
                  subSection
                  filters={filters}
                  setFilters={setFilters}
                >
                  <MultiValueInputCondition
                    id="imdb_id"
                    name="imdbIdSearch"
                    filters={filters}
                    setFilters={setFilters}
                  />
                </CollapsibleSection>
                <CollapsibleSection
                  title="Duration (Minutes)"
                  id="duration_minutes"
                  subSection
                  filters={filters}
                  setFilters={setFilters}
                >
                  <NumberBetweenInput
                    id="duration_minutes"
                    name="duration"
                    filters={filters}
                    setFilters={setFilters}
                  />
                </CollapsibleSection>
                <CollapsibleSection
                  title="Video Language"
                  id="video_language"
                  subSection
                  filters={filters}
                  setFilters={setFilters}
                >
                  <LanguageCodes
                    id="video_language"
                    name="videoLanguage"
                    label="Language"
                    defaultValue={getDefaultValue('video_language')}
                    onChange={(e) => handleChange(e, 'video_language')}
                    sx={{ width: '100%', my: 1 }}
                  />
                  <VideoFilterSelectCondition
                    id="video_language_conditionType"
                    name="videoLanguageConditionType"
                    label="Condition"
                    defaultValue={
                      filters.video_language?.condition || 'EQUALS_TO'
                    }
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        video_language: {
                          ...prev.video_language,
                          condition: e.target.value,
                          type: 'selectType',
                          value: prev.video_language?.value || '',
                          apply: prev.video_language?.apply || false,
                        },
                      }))
                    }
                    validOptions={Object.keys(FILTER_SELECT_FIELD_CONDITION)}
                    sx={{ width: '100%', my: 1 }}
                  />
                </CollapsibleSection>
                <CollapsibleSection
                  title="Color"
                  id="color"
                  subSection
                  filters={filters}
                  setFilters={setFilters}
                  sx={{
                    borderBottom: 1,
                    borderWidth: '1px',
                    borderColor: 'gray',
                  }}
                >
                  <Booleans
                    id="color"
                    name="color"
                    label="Color"
                    defaultValue={getDefaultValue('color')}
                    onChange={(e) => handleChange(e, 'color')}
                    sx={{ width: '100%', my: 1 }}
                  />
                </CollapsibleSection>
              </Box>
            </CollapsibleSection>
            <CollapsibleSection
              title="Video Classification"
              appliedFilter={hasAppliedFilters('videoClassification')}
            >
              <Box sx={{ marginBottom: 1 }}>
                <CollapsibleSection
                  title="Collection Name"
                  id="c.name"
                  subSection
                  filters={filters}
                  setFilters={setFilters}
                >
                  <FilterInputCondition
                    id="c.name"
                    name="collectionNameSearch"
                    filters={filters}
                    setFilters={setFilters}
                  />
                </CollapsibleSection>
                <CollapsibleSection
                  title="Year"
                  id="year"
                  subSection
                  filters={filters}
                  setFilters={setFilters}
                >
                  <NumberBetweenInput
                    id="year"
                    name="year"
                    filters={filters}
                    setFilters={setFilters}
                  />
                </CollapsibleSection>
                <CollapsibleSection
                  title="Decade"
                  id="decade"
                  subSection
                  filters={filters}
                  setFilters={setFilters}
                >
                  <Decades
                    id="decade"
                    name="decade"
                    label="Decade"
                    defaultValue={getDefaultValue('decade')}
                    onChange={(e) => handleChange(e, 'decade')}
                    validOptions={Object.keys(DECADES)}
                    sx={{ width: '100%', my: 1 }}
                  />
                </CollapsibleSection>
                <CollapsibleSection
                  title="Country"
                  id="v.country"
                  subSection
                  filters={filters}
                  setFilters={setFilters}
                >
                  <Countries
                    id="v.country"
                    name="v.country"
                    label="Country"
                    defaultValue={getDefaultValue('v.country')}
                    onChange={(e) => handleChange(e, 'v.country')}
                    sx={{ width: '100%', my: 1 }}
                  />
                </CollapsibleSection>
                <CollapsibleSection
                  title="Region"
                  id="region"
                  subSection
                  filters={filters}
                  setFilters={setFilters}
                  sx={{
                    borderBottom: 1,
                    borderWidth: '1px',
                    borderColor: 'gray',
                  }}
                >
                  <Regions
                    id="region"
                    name="region"
                    label="Region"
                    defaultValue={getDefaultValue('region')}
                    onChange={(e) => handleChange(e, 'region')}
                    sx={{ width: '100%', my: 1 }}
                  />
                </CollapsibleSection>
              </Box>
            </CollapsibleSection>
            <CollapsibleSection
              title="Series Attributes"
              appliedFilter={hasAppliedFilters('seriesAttributes')}
            >
              <Box sx={{ marginBottom: 1 }}>
                <CollapsibleSection
                  title="Series Name"
                  id="s.title"
                  subSection
                  filters={filters}
                  setFilters={setFilters}
                >
                  <FilterInputCondition
                    id="s.title"
                    name="seriesNameSearch"
                    filters={filters}
                    setFilters={setFilters}
                  />
                </CollapsibleSection>
                <CollapsibleSection
                  title="Season Number"
                  id="season"
                  subSection
                  filters={filters}
                  setFilters={setFilters}
                >
                  <FilterInputCondition
                    id="season"
                    name="seasonNumberSearch"
                    filters={filters}
                    setFilters={setFilters}
                  />
                </CollapsibleSection>
                <CollapsibleSection
                  title="Episode Number"
                  sx={{
                    borderBottom: 1,
                    borderWidth: '1px',
                    borderColor: 'gray',
                  }}
                  id="episode_num"
                  subSection
                  filters={filters}
                  setFilters={setFilters}
                >
                  <FilterInputCondition
                    id="episode_num"
                    name="episodeNumberSearch"
                    filters={filters}
                    setFilters={setFilters}
                  />
                </CollapsibleSection>
              </Box>
            </CollapsibleSection>
            <CollapsibleSection
              title="Owner"
              appliedFilter={hasAppliedFilters('owner')}
            >
              <Box sx={{ marginBottom: 1 }}>
                <CollapsibleSection
                  title="Distributor Name"
                  id="d.username"
                  subSection
                  filters={filters}
                  setFilters={setFilters}
                >
                  <FilterInputCondition
                    id="d.username"
                    name="d.username"
                    filters={filters}
                    setFilters={setFilters}
                  />
                </CollapsibleSection>
                <CollapsibleSection
                  title="Title SKU"
                  id="distributor_sku"
                  subSection
                  filters={filters}
                  setFilters={setFilters}
                >
                  <MultiValueInputCondition
                    id="distributor_sku"
                    name="distributor_sku"
                    filters={filters}
                    setFilters={setFilters}
                  />
                </CollapsibleSection>
                <CollapsibleSection
                  title="Series SKU"
                  id="distributor_sku_series"
                  subSection
                  filters={filters}
                  setFilters={setFilters}
                >
                  <MultiValueInputCondition
                    id="distributor_sku_series"
                    name="distributorSkuSeries"
                    filters={filters}
                    setFilters={setFilters}
                  />
                </CollapsibleSection>
                <CollapsibleSection
                  title="Contract Number"
                  id="contract_no"
                  subSection
                  filters={filters}
                  setFilters={setFilters}
                >
                  <FilterInputCondition
                    id="contract_no"
                    name="contractNo"
                    filters={filters}
                    setFilters={setFilters}
                  />
                </CollapsibleSection>
                <CollapsibleSection
                  title="Royality"
                  id="d.royality"
                  subSection
                  filters={filters}
                  setFilters={setFilters}
                >
                  <NumberBetweenInput
                    id="d.royality"
                    name="d.royality"
                    filters={filters}
                    setFilters={setFilters}
                  />
                </CollapsibleSection>
                <CollapsibleSection
                  title="Label"
                  id="studio"
                  subSection
                  filters={filters}
                  setFilters={setFilters}
                  sx={{
                    borderBottom: 1,
                    borderWidth: '1px',
                    borderColor: 'gray',
                  }}
                >
                  <FilterInputCondition
                    id="studio"
                    name="studio"
                    filters={filters}
                    setFilters={setFilters}
                  />
                </CollapsibleSection>
              </Box>
            </CollapsibleSection>
            <CollapsibleSection
              title="Rights Management"
              appliedFilter={hasAppliedFilters('rightsManagement')}
            >
              <Box sx={{ marginBottom: 1 }}>
                <CollapsibleSection
                  title="License Expiration Date"
                  id="date_license_expiration"
                  subSection
                  filters={filters}
                  setFilters={setFilters}
                >
                  <DateBetweenInput
                    id="date_license_expiration"
                    name="dateLicenseExpire"
                    filters={filters}
                    setFilters={setFilters}
                  />
                </CollapsibleSection>
                <CollapsibleSection
                  title="AVOD Street Date"
                  id="avod"
                  subSection
                  filters={filters}
                  setFilters={setFilters}
                >
                  <DateBetweenInput
                    id="avod"
                    name="avod"
                    filters={filters}
                    setFilters={setFilters}
                  />
                </CollapsibleSection>
                <CollapsibleSection
                  title="SVOD Street Date"
                  id="svod"
                  subSection
                  filters={filters}
                  setFilters={setFilters}
                >
                  <DateBetweenInput
                    id="svod"
                    name="svod"
                    filters={filters}
                    setFilters={setFilters}
                  />
                </CollapsibleSection>
                <CollapsibleSection
                  title="TVOD Street Date"
                  id="tvod"
                  subSection
                  filters={filters}
                  setFilters={setFilters}
                >
                  <DateBetweenInput
                    id="tvod"
                    name="tvod"
                    filters={filters}
                    setFilters={setFilters}
                  />
                </CollapsibleSection>
                <CollapsibleSection
                  title="AVOD License"
                  id="avod_validated"
                  subSection
                  filters={filters}
                  setFilters={setFilters}
                >
                  <RestrictedContent
                    id="avod_validated"
                    name="avodLicense"
                    label="AVOD License"
                    defaultValue={getDefaultValue('avod_validated')}
                    onChange={(e) => handleChange(e, 'avod_validated')}
                    sx={{ width: '100%', my: 1 }}
                  />
                </CollapsibleSection>
                <CollapsibleSection
                  title="TVOD Enabled"
                  id="tvod_enabled"
                  subSection
                  filters={filters}
                  setFilters={setFilters}
                >
                  <Booleans
                    id="tvod_enabled"
                    name="tvodEnabled"
                    label="TVOD Enabled"
                    defaultValue={getDefaultValue('tvod_enabled')}
                    onChange={(e) => handleChange(e, 'tvod_enabled')}
                    sx={{ width: '100%', my: 1 }}
                  />
                </CollapsibleSection>
                <CollapsibleSection
                  title="TVOD Price"
                  id="tvod_iap"
                  subSection
                  filters={filters}
                  setFilters={setFilters}
                >
                  <TVODPrice
                    id="tvod_iap"
                    name="tvodIap"
                    label="TVOD Price"
                    defaultValue={getDefaultValue('tvod_iap')}
                    onChange={(e) => handleChange(e, 'tvod_iap')}
                    sx={{ width: '100%', my: 1 }}
                  />
                </CollapsibleSection>
                <CollapsibleSection
                  title="GeoFencing Allow"
                  id="geofencing_allow"
                  subSection
                  filters={filters}
                  setFilters={setFilters}
                >
                  <FilterInputCondition
                    id="geofencing_allow"
                    name="geoFencingAllow"
                    filters={filters}
                    setFilters={setFilters}
                  />
                </CollapsibleSection>
                <CollapsibleSection
                  title="GeoFencing Deny"
                  id="geofencing_deny"
                  subSection
                  filters={filters}
                  setFilters={setFilters}
                  sx={{
                    borderBottom: 1,
                    borderWidth: '1px',
                    borderColor: 'gray',
                  }}
                >
                  <FilterInputCondition
                    id="geofencing_deny"
                    name="geoFencingDeny"
                    filters={filters}
                    setFilters={setFilters}
                  />
                </CollapsibleSection>
              </Box>
            </CollapsibleSection>
            <CollapsibleSection
              title="Video/Ad Live Status"
              appliedFilter={hasAppliedFilters('videoAdLiveStatus')}
            >
              <Box sx={{ marginBottom: 1 }}>
                <CollapsibleSection
                  title="Ad Timing Status"
                  id="ad_timing_status"
                  subSection
                  filters={filters}
                  setFilters={setFilters}
                >
                  <AdTimingStatus
                    id="ad_timing_status"
                    name="adTimingStatus"
                    label="Ad Timing Status"
                    defaultValue={getDefaultValue('ad_timing_status')}
                    onChange={(e) => handleChange(e, 'ad_timing_status')}
                    sx={{ width: '100%', my: 1 }}
                  />
                </CollapsibleSection>
                <CollapsibleSection
                  title="AVOD Enabled"
                  id="avod_enabled"
                  subSection
                  filters={filters}
                  setFilters={setFilters}
                >
                  <Booleans
                    id="avod_enabled"
                    name="avodEnabled"
                    label="avod Enabled"
                    defaultValue={getDefaultValue('avod_enabled')}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        avod_enabled: {
                          ...(prev.avod_enabled || {}),
                          value: e?.target?.value || e,
                          condition: 'EQUALS_TO',
                          type: 'monitizationType',
                          apply: prev.avod_enabled?.apply || false,
                        },
                      }))
                    }
                    sx={{ width: '100%', my: 1 }}
                  />
                </CollapsibleSection>
                <CollapsibleSection
                  title="Offline"
                  id="offline"
                  subSection
                  filters={filters}
                  setFilters={setFilters}
                >
                  <Booleans
                    id="offline"
                    name="offline"
                    label="Offline"
                    defaultValue={getDefaultValue('offline')}
                    onChange={(e) => handleChange(e, 'offline')}
                    sx={{ width: '100%', my: 1 }}
                  />
                </CollapsibleSection>
                <CollapsibleSection
                  title="Offline Status"
                  id="offline_reason"
                  subSection
                  filters={filters}
                  setFilters={setFilters}
                  sx={{
                    borderBottom: 1,
                    borderWidth: '1px',
                    borderColor: 'gray',
                  }}
                >
                  <FilterInputCondition
                    id="offline_reason"
                    name="offlineReasonSearch"
                    filters={filters}
                    setFilters={setFilters}
                  />
                </CollapsibleSection>
              </Box>
            </CollapsibleSection>
            <CollapsibleSection
              title="Editorial"
              appliedFilter={hasAppliedFilters('editorial')}
            >
              <Box sx={{ marginBottom: 1 }}>
                <CollapsibleSection
                  title="Restricted Content"
                  id="restricted_content"
                  subSection
                  filters={filters}
                  setFilters={setFilters}
                >
                  <RestrictedContent
                    id="restricted_content"
                    name="restrictedContent"
                    label="Restricted Content"
                    defaultValue={getDefaultValue('restricted_content')}
                    onChange={(e) => handleChange(e, 'restricted_content')}
                    sx={{ width: '100%', my: 1 }}
                  />
                </CollapsibleSection>
                <CollapsibleSection
                  title="Restricted Content Reason"
                  id="restricted_content_reason"
                  subSection
                  filters={filters}
                  setFilters={setFilters}
                >
                  <RestrictedContentReason
                    id="restricted_content_reason"
                    name="restrictedContentReason"
                    label="Restricted Content Reason"
                    defaultValue={getDefaultValue('restricted_content_reason')}
                    onChange={(e) =>
                      handleChange(e, 'restricted_content_reason')
                    }
                    sx={{ width: '100%', my: 1 }}
                  />
                </CollapsibleSection>
                <CollapsibleSection
                  title="Exclude from DB"
                  id="exclude_from_db"
                  subSection
                  filters={filters}
                  setFilters={setFilters}
                  sx={{
                    borderBottom: 1,
                    borderWidth: '1px',
                    borderColor: 'gray',
                  }}
                >
                  <Booleans
                    id="exclude_from_db"
                    name="excludeFromDb"
                    label="Exclude From DB"
                    defaultValue={getDefaultValue('exclude_from_db')}
                    onChange={(e) => handleChange(e, 'exclude_from_db')}
                    sx={{ width: '100%', my: 1 }}
                  />
                </CollapsibleSection>
              </Box>
            </CollapsibleSection>
            <CollapsibleSection
              title="Rating"
              appliedFilter={hasAppliedFilters('rating')}
            >
              <Box sx={{ marginBottom: 1 }}>
                <CollapsibleSection
                  title="Good-Bad"
                  id="good_bad"
                  subSection
                  filters={filters}
                  setFilters={setFilters}
                >
                  <GoodBad
                    id="good_bad"
                    name="goodBad"
                    label="Good Bad"
                    defaultValue={getDefaultValue('good_bad')}
                    onChange={(e) => handleChange(e, 'good_bad')}
                    sx={{ width: '100%', my: 1 }}
                  />
                </CollapsibleSection>
                <CollapsibleSection
                  title="Trashy"
                  id="trashy"
                  subSection
                  filters={filters}
                  setFilters={setFilters}
                >
                  <Trashy
                    id="trashy"
                    name="trashy"
                    label="Trashy"
                    defaultValue={getDefaultValue('trashy')}
                    onChange={(e) => handleChange(e, 'trashy')}
                    sx={{ width: '100%', my: 1 }}
                  />
                </CollapsibleSection>
                <CollapsibleSection
                  title="5-Star Rating"
                  id="five_star_rating"
                  subSection
                  filters={filters}
                  setFilters={setFilters}
                >
                  <NumberBetweenInput
                    id="five_star_rating"
                    name="fiveStarRating"
                    filters={filters}
                    setFilters={setFilters}
                  />
                </CollapsibleSection>
                <CollapsibleSection
                  title="IMDB Rating"
                  sx={{
                    borderBottom: 1,
                    borderWidth: '1px',
                    borderColor: 'gray',
                  }}
                  id="mpaa_rating"
                  subSection
                  filters={filters}
                  setFilters={setFilters}
                >
                  <MpaaRating
                    id="mpaa_rating"
                    name="mpaaRating"
                    label="IMDB Rating"
                    defaultValue={getDefaultValue('mpaa_rating')}
                    onChange={(e) => handleChange(e, 'mpaa_rating')}
                    sx={{ width: '100%', my: 1 }}
                  />
                </CollapsibleSection>
              </Box>
            </CollapsibleSection>
            <CollapsibleSection
              title="Public Domain"
              appliedFilter={hasAppliedFilters('publicDomain')}
            >
              <Box sx={{ marginBottom: 1 }}>
                <CollapsibleSection
                  title="Public Domain"
                  id="public_domain"
                  subSection
                  filters={filters}
                  setFilters={setFilters}
                >
                  <PublicDomainAutocomplete
                    id="public_domain"
                    name="public_domain"
                    label="Public Domain"
                    defaultValue={getDefaultValue('public_domain')}
                    multiple
                    onChange={(e) => {
                      setFilters((prev) => ({
                        ...prev,
                        public_domain: {
                          ...(prev.public_domain || {}),
                          value: e?.target?.value || e,
                          condition: 'EQUALS_TO',
                          type: 'multiSelectType',
                          apply: prev.public_domain?.apply || false,
                        },
                      }));
                    }}
                    sx={{ width: '100%', my: 1 }}
                  />
                </CollapsibleSection>
                <CollapsibleSection
                  title="Custom Royality"
                  id="custom_royality"
                  subSection
                  filters={filters}
                  setFilters={setFilters}
                  sx={{
                    borderBottom: 1,
                    borderWidth: '1px',
                    borderColor: 'gray',
                  }}
                >
                  <NumberBetweenInput
                    id="custom_royality"
                    name="customRoyality"
                    filters={filters}
                    setFilters={setFilters}
                  />
                </CollapsibleSection>
              </Box>
            </CollapsibleSection>
            <CollapsibleSection
              title="Tools"
              appliedFilter={hasAppliedFilters('tools')}
            >
              <Box sx={{ marginBottom: 1 }}>
                <CollapsibleSection
                  title="Duplicate IMDB"
                  id="duplicate_imdb"
                  subSection
                  filters={filters}
                  setFilters={setFilters}
                >
                  <Booleans
                    id="duplicate_imdb"
                    name="duplicateImdb"
                    label="Duplicate IMDB"
                    defaultValue={getDefaultValue('duplicate_imdb')}
                    onChange={(e) => handleChange(e, 'duplicate_imdb')}
                    sx={{ width: '100%', my: 1 }}
                  />
                </CollapsibleSection>

                <Box
                  sx={{
                    marginX: 2,
                    paddingLeft: 0.5,
                    paddingRight: 1,
                    borderRight: 1,
                    borderTop: 1,
                    borderLeft: 1,
                    borderWidth: '1px',
                    borderColor: 'gray',
                  }}
                >
                  <StyledItem>
                    <IconButton aria-label="expand-more" size="small" disabled>
                      <Iconify icon="ic:round-expand-more" width={25} />
                    </IconButton>

                    <ListItemText
                      disableTypography
                      primary="Show License Expired Videos"
                      sx={{ marginLeft: 0.25 }}
                    />

                    <Checkbox
                      name="licenese_expired_videos"
                      onChange={(e) => {
                        setFilters((prev) => ({
                          ...prev,
                          licenese_expired_videos: {
                            ...prev.licenese_expired_videos,
                            title: 'Show License Expired Videos',
                            condition: 'EQUALS_TO',
                            type: 'checkbox',
                            value: e.target.checked,
                            apply: true,
                          },
                        }));
                      }}
                      checked={filters.licenese_expired_videos?.value || false}
                      value={filters.licenese_expired_videos?.value || false}
                    />
                  </StyledItem>
                </Box>

                <CollapsibleSection
                  title="Always Published"
                  id="always_published"
                  sx={{
                    borderBottom: 1,
                    borderWidth: '1px',
                    borderColor: 'gray',
                  }}
                  subSection
                  filters={filters}
                  setFilters={setFilters}
                >
                  <AlwaysPublished
                    id="always_published"
                    name="always_published"
                    label="Always Published"
                    defaultValue={getDefaultValue('always_published')}
                    onChange={(e) => handleChange(e, 'always_published')}
                    sx={{ width: '100%', my: 1 }}
                  />
                </CollapsibleSection>
              </Box>
            </CollapsibleSection>
            <CollapsibleSection
              title="Cast/Crew"
              appliedFilter={hasAppliedFilters('castCrew')}
            >
              <Box sx={{ marginBottom: 1 }}>
                <CollapsibleSection
                  title="Director"
                  id="directors"
                  subSection
                  filters={filters}
                  setFilters={setFilters}
                >
                  <FilterInputCondition
                    id="directors"
                    name="directorSearch"
                    filters={filters}
                    setFilters={setFilters}
                  />
                </CollapsibleSection>
                <CollapsibleSection
                  title="Writer"
                  id="writers"
                  subSection
                  filters={filters}
                  setFilters={setFilters}
                >
                  <FilterInputCondition
                    id="writers"
                    name="writerSearch"
                    filters={filters}
                    setFilters={setFilters}
                  />
                </CollapsibleSection>
                <CollapsibleSection
                  title="Cast"
                  id="cast"
                  subSection
                  filters={filters}
                  setFilters={setFilters}
                  sx={{
                    borderBottom: 1,
                    borderWidth: '1px',
                    borderColor: 'gray',
                  }}
                >
                  <MultiValueInputCondition
                    id="cast"
                    name="castSearch"
                    filters={filters}
                    setFilters={setFilters}
                  />
                </CollapsibleSection>
              </Box>
            </CollapsibleSection>
            <CollapsibleSection
              title="Admin"
              appliedFilter={hasAppliedFilters('admin')}
            >
              <Box sx={{ marginBottom: 1 }}>
                <CollapsibleSection
                  title="MM Notes"
                  id="mm_notes"
                  subSection
                  filters={filters}
                  setFilters={setFilters}
                >
                  <FilterInputCondition
                    id="mm_notes"
                    name="MMNotes"
                    filters={filters}
                    setFilters={setFilters}
                  />
                </CollapsibleSection>
                <CollapsibleSection
                  title="MS Notes"
                  sx={{
                    borderBottom: 1,
                    borderWidth: '1px',
                    borderColor: 'gray',
                  }}
                  id="ms_notes"
                  subSection
                  filters={filters}
                  setFilters={setFilters}
                >
                  <FilterInputCondition
                    id="ms_notes"
                    name="MSNotes"
                    filters={filters}
                    setFilters={setFilters}
                  />
                </CollapsibleSection>
              </Box>
            </CollapsibleSection>
            <CollapsibleSection
              title="Metadata Status"
              appliedFilter={hasAppliedFilters('metadataStatus')}
            >
              <Box sx={{ marginBottom: 1 }}>
                {' '}
                <CollapsibleSection
                  title="Genre Status"
                  id="genre_status"
                  subSection
                  filters={filters}
                  setFilters={setFilters}
                >
                  <GenreStatus
                    id="genre_status"
                    name="genreStatus"
                    label="Genre Status"
                    defaultValue={getDefaultValue('genre_status')}
                    onChange={(e) => handleChange(e, 'genre_status')}
                    sx={{ width: '100%', my: 1 }}
                  />
                </CollapsibleSection>
                <CollapsibleSection
                  title="Description Status"
                  id="description_status"
                  subSection
                  filters={filters}
                  setFilters={setFilters}
                >
                  <FilterInputCondition
                    id="description_status"
                    name="descriptionStatusSearch"
                    filters={filters}
                    setFilters={setFilters}
                  />
                </CollapsibleSection>
                <CollapsibleSection
                  title="Poster Status"
                  id="poster_16x9_status"
                  subSection
                  filters={filters}
                  setFilters={setFilters}
                  sx={{
                    borderBottom: 1,
                    borderWidth: '1px',
                    borderColor: 'gray',
                  }}
                >
                  <Poster16x9Status
                    id="poster_16x9_status"
                    name="poster16x9Status"
                    label="Poster Status"
                    defaultValue={getDefaultValue('poster_16x9_status')}
                    onChange={(e) => handleChange(e, 'poster_16x9_status')}
                    sx={{ width: '100%', my: 1 }}
                  />
                </CollapsibleSection>
              </Box>
            </CollapsibleSection>
            <CollapsibleSection
              title="Asset URL"
              appliedFilter={hasAppliedFilters('assetUrl')}
            >
              <Box sx={{ marginBottom: 1 }}>
                <CollapsibleSection
                  title="Video URL"
                  id="video_url"
                  subSection
                  filters={filters}
                  setFilters={setFilters}
                >
                  <FilterInputCondition
                    id="video_url"
                    name="videoUrl"
                    filters={filters}
                    setFilters={setFilters}
                  />
                </CollapsibleSection>
                <CollapsibleSection
                  title="Trailer URL"
                  id="trailer_url"
                  subSection
                  filters={filters}
                  setFilters={setFilters}
                >
                  <FilterInputCondition
                    id="trailer_url"
                    name="trailerUrl"
                    filters={filters}
                    setFilters={setFilters}
                  />
                </CollapsibleSection>
                <CollapsibleSection
                  title="Poster (16x9) URL"
                  id="poster_16x9_url"
                  subSection
                  filters={filters}
                  setFilters={setFilters}
                >
                  <FilterInputCondition
                    id="poster_16x9_url"
                    name="poster16x9Url"
                    filters={filters}
                    setFilters={setFilters}
                  />
                </CollapsibleSection>
                <CollapsibleSection
                  title="Subtitle (EN) URL"
                  sx={{
                    borderBottom: 1,
                    borderWidth: '1px',
                    borderColor: 'gray',
                  }}
                  id="subtitle_en_url"
                  subSection
                  filters={filters}
                  setFilters={setFilters}
                >
                  <FilterInputCondition
                    id="subtitle_en_url"
                    name="subtitleUrl"
                    filters={filters}
                    setFilters={setFilters}
                  />
                </CollapsibleSection>
              </Box>
            </CollapsibleSection>
            <CollapsibleSection
              title="Asset URL Status"
              appliedFilter={hasAppliedFilters('assetUrlStatus')}
            >
              <Box sx={{ marginBottom: 1 }}>
                <CollapsibleSection
                  title="Video URL Check"
                  id="quality_video"
                  subSection
                  filters={filters}
                  setFilters={setFilters}
                >
                  <UrlCheckType
                    id="quality_video"
                    name="videoUrlCheck"
                    label="Video URL Check"
                    defaultValue={getDefaultValue('quality_video')}
                    onChange={(e) => handleChange(e, 'quality_video')}
                    validOptions={Object.keys(URL_CHECK)}
                    sx={{ width: '100%', my: 1 }}
                  />
                </CollapsibleSection>
                <CollapsibleSection
                  title="Subtitles URL Check"
                  id="quality_subtitle"
                  subSection
                  filters={filters}
                  setFilters={setFilters}
                >
                  <UrlCheckType
                    id="quality_subtitle"
                    name="SubtitlesUrlCheck"
                    label="Subtitles URL Check"
                    defaultValue={getDefaultValue('quality_subtitle')}
                    onChange={(e) => handleChange(e, 'quality_subtitle')}
                    validOptions={Object.keys(URL_CHECK)}
                    sx={{ width: '100%', my: 1 }}
                  />
                </CollapsibleSection>
                <CollapsibleSection
                  title="Poster 16x9 URL Check"
                  id="quality_poster_16x9"
                  subSection
                  filters={filters}
                  setFilters={setFilters}
                >
                  <UrlCheckType
                    id="quality_poster_16x9"
                    name="poster16x9UrlCheck"
                    label="Poster 16x9 URL Check"
                    defaultValue={getDefaultValue('quality_poster_16x9')}
                    onChange={(e) => handleChange(e, 'quality_poster_16x9')}
                    validOptions={Object.keys(URL_CHECK)}
                    sx={{ width: '100%', my: 1 }}
                  />
                </CollapsibleSection>
                <CollapsibleSection
                  title="Trailer URL Check"
                  id="quality_trailer"
                  subSection
                  filters={filters}
                  setFilters={setFilters}
                >
                  <UrlCheckType
                    id="quality_trailer"
                    name="trailerUrlCheck"
                    label="Trailer URL Check"
                    defaultValue={getDefaultValue('quality_trailer')}
                    onChange={(e) => handleChange(e, 'quality_trailer')}
                    validOptions={Object.keys(URL_CHECK)}
                    sx={{ width: '100%', my: 1 }}
                  />
                </CollapsibleSection>
                <CollapsibleSection
                  title="Source URL Check"
                  sx={{
                    borderBottom: 1,
                    borderWidth: '1px',
                    borderColor: 'gray',
                  }}
                  id="quality_source_url"
                  subSection
                  filters={filters}
                  setFilters={setFilters}
                >
                  <UrlCheckType
                    id="quality_source_url"
                    name="sourceUrlCheck"
                    label="Source URL Check"
                    defaultValue={getDefaultValue('quality_source_url')}
                    onChange={(e) => handleChange(e, 'quality_source_url')}
                    validOptions={Object.keys(URL_CHECK)}
                    sx={{ width: '100%', my: 1 }}
                  />
                </CollapsibleSection>
              </Box>
            </CollapsibleSection>
            <CollapsibleSection
              title="Analytics"
              appliedFilter={hasAppliedFilters('analytics')}
              sx={{
                borderBottom: 1,
                borderWidth: '1px',
                borderColor: 'gray',
              }}
            >
              <Box sx={{ marginBottom: 1 }}>
                <CollapsibleSection
                  title="AVOD Weekly Min Watched"
                  id="avod_watched"
                  subSection
                  filters={filters}
                  setFilters={setFilters}
                >
                  <NumberBetweenInput
                    id="avod_watched"
                    name="avodWatched"
                    filters={filters}
                    setFilters={setFilters}
                  />
                </CollapsibleSection>
                <CollapsibleSection
                  title="SVOD Weekly Min Watched"
                  id="svod_watched"
                  subSection
                  filters={filters}
                  setFilters={setFilters}
                  sx={{
                    borderBottom: 1,
                    borderWidth: '1px',
                    borderColor: 'gray',
                  }}
                >
                  <NumberBetweenInput
                    id="svod_watched"
                    name="svodWatched"
                    filters={filters}
                    setFilters={setFilters}
                  />
                </CollapsibleSection>
              </Box>
            </CollapsibleSection>
          </Stack>
        </form>
      </Scrollbar>
      <Divider />

      <Stack spacing={1} sx={{ p: 2, flex: 1 }}>
        <Button
          fullWidth
          form="list-data-filters"
          type="submit"
          color="primary"
          variant="contained"
          onClick={handleApplyFilters}
          disabled={isFormSubmitted}
        >
          Save Videos
        </Button>
      </Stack>
    </>
  );
};

FilterPlaylistVideos.propTypes = {
  values: PropTypes.shape().isRequired,
  onCancel: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  from: PropTypes.string,
  isFormSubmitted: PropTypes.bool.isRequired,
};

FilterPlaylistVideos.defaultProps = {
  from: '',
};

export default FilterPlaylistVideos;
